import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCatalog } from "@/providers/CatalogProvider";
import { useStudentQuery } from "@/hooks/useStudents";
import {
  useScaleAssessmentSession,
  type ScaleAssessmentSaveStatus
} from "@/hooks/useScaleAssessmentSession";
import type { AssessmentEntry } from "@/types/assessment";
import type { ScaleEntry } from "@/types/scale";
import { VoiceInputButton } from "@/components/common/VoiceInputButton";
import { AssessmentBreadcrumbs } from "@/components/assessments/AssessmentBreadcrumbs";
import { AssessmentEntryModeSwitch } from "@/components/assessments/AssessmentEntryModeSwitch";
import { AssessmentStartPanel } from "@/components/assessments/AssessmentStartPanel";
import { nextUnscoredEntryCode, requiredEntryCodes } from "@/utils/assessment-progress";
import { assessmentBelongsToStudent } from "@/utils/student-workspace";

interface LongTermGroup {
  entry: ScaleEntry;
  shortEntries: ScaleEntry[];
}

interface SubdomainView {
  domainCode: string;
  domainName: string;
  subdomainCode: string;
  subdomainLabel: string;
  longTerms: LongTermGroup[];
}

interface DomainView {
  domainCode: string;
  domainName: string;
  subdomains: SubdomainView[];
}

export function ScaleEvaluationPage(): JSX.Element {
  const { scaleLibrary } = useCatalog();
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams] = useSearchParams();
  const requestedAssessmentId = searchParams.get("assessmentId") || undefined;

  const [activeEntryCode, setActiveEntryCode] = useState<string | undefined>(undefined);
  const [manualExpandedLongGoals, setManualExpandedLongGoals] = useState<Set<string>>(new Set());
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(new Set());
  const [collapsedSubdomains, setCollapsedSubdomains] = useState<Set<string>>(new Set());

  useEffect(() => {
    setManualExpandedLongGoals(new Set());
    setActiveEntryCode(undefined);
  }, [requestedAssessmentId]);

  const {
    assessment,
    assessmentLoading,
    assessmentError,
    entries,
    entriesLoading,
    saveEntry,
    flushEntries,
    saveStatus,
    markStatus,
    marking
  } = useScaleAssessmentSession(requestedAssessmentId, studentId);
  const studentQuery = useStudentQuery(studentId);

  const domainTree = useMemo<DomainView[]>(() => {
    return scaleLibrary.domains.map((domain) => ({
      domainCode: domain.domainCode,
      domainName: domain.domainName,
      subdomains: domain.subdomains.map((subdomain) => {
        const longTerms = subdomain.longTermGoals
          .map((goal) => {
            const entry = scaleLibrary.entryIndex.get(goal.code);
            if (!entry) {
              return null;
            }
            const shortEntries = goal.children
              .map((child) => scaleLibrary.entryIndex.get(child.code))
              .filter((child): child is ScaleEntry => Boolean(child));
            return { entry, shortEntries };
          })
          .filter((item): item is LongTermGroup => Boolean(item));
        return {
          domainCode: domain.domainCode,
          domainName: domain.domainName,
          subdomainCode: subdomain.subdomainCode,
          subdomainLabel: subdomain.subdomainLabel,
          longTerms
        };
      })
    }));
  }, [scaleLibrary]);

  const entriesMap = useMemo(() => {
    const map = new Map<string, AssessmentEntry>();
    entries.forEach((entry) => map.set(entry.entryCode, entry));
    return map;
  }, [entries]);

  const entryRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [pendingScrollCode, setPendingScrollCode] = useState<string>();
  const registerEntryRef = useCallback((code: string, node: HTMLElement | null) => {
    if (node) {
      entryRefs.current.set(code, node);
    } else {
      entryRefs.current.delete(code);
    }
  }, []);

  const scrollToEntry = useCallback(
    (code: string) => {
      const target = entryRefs.current.get(code);
      if (target) {
        scrollIntoMainContent(target);
        setActiveEntryCode(code);
      }
    },
    [],
  );

  const hasScore = useCallback((record?: AssessmentEntry) => record?.score !== null && record?.score !== undefined, []);

  const shouldDisplayShortTerms = useCallback(
    (group: LongTermGroup) => {
      if (group.shortEntries.length === 0) {
        return false;
      }
      const longRecord = entriesMap.get(group.entry.code);
      if (longRecord?.score === 0 || longRecord?.score === 1) {
        return true;
      }
      if (manualExpandedLongGoals.has(group.entry.code)) {
        return true;
      }
      return group.shortEntries.some((child) => {
        const record = entriesMap.get(child.code);
        return hasScore(record) || Boolean(record?.comment);
      });
    },
    [entriesMap, hasScore, manualExpandedLongGoals],
  );

  const longTermShortMap = useMemo(() => {
    const map = new Map<string, boolean>();
    domainTree.forEach((domain) =>
      domain.subdomains.forEach((subdomain) =>
        subdomain.longTerms.forEach((group) => {
          map.set(group.entry.code, shouldDisplayShortTerms(group));
        }),
      ),
    );
    return map;
  }, [domainTree, shouldDisplayShortTerms]);

  const visibleEntryCodes = useMemo(() => {
    const codes: string[] = [];
    domainTree.forEach((domain) =>
      domain.subdomains.forEach((subdomain) =>
        subdomain.longTerms.forEach((group) => {
          codes.push(group.entry.code);
          if (longTermShortMap.get(group.entry.code)) {
            group.shortEntries.forEach((child) => codes.push(child.code));
          }
        }),
      ),
    );
    return codes;
  }, [domainTree, longTermShortMap]);

  useEffect(() => {
    if (!activeEntryCode && visibleEntryCodes.length > 0) {
      setActiveEntryCode(visibleEntryCodes[0]);
    }
  }, [activeEntryCode, visibleEntryCodes]);

  useEffect(() => {
    if (!pendingScrollCode) return;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const target = entryRefs.current.get(pendingScrollCode);
        if (target) {
          scrollIntoMainContent(target);
          setActiveEntryCode(pendingScrollCode);
        }
        setPendingScrollCode(undefined);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [pendingScrollCode, visibleEntryCodes]);

  const completedEntries = visibleEntryCodes.filter((code) => hasScore(entriesMap.get(code))).length;
  const totalEntries = visibleEntryCodes.length;
  const overallProgress = totalEntries > 0 ? Math.round((completedEntries / totalEntries) * 100) : 0;

  const navTree = useMemo(() => {
    return domainTree.map((domain) => {
      let domainCompleted = 0;
      let domainTotal = 0;
      const subdomains = domain.subdomains.map((subdomain) => {
        const visibleCodes: string[] = [];
        const longTerms = subdomain.longTerms.map((group) => {
          const showShorts = longTermShortMap.get(group.entry.code) ?? false;
          visibleCodes.push(group.entry.code);
          if (showShorts) {
            group.shortEntries.forEach((child) => visibleCodes.push(child.code));
          }
          return { ...group, showShorts };
        });
        const completed = visibleCodes.filter((code) => hasScore(entriesMap.get(code))).length;
        domainCompleted += completed;
        domainTotal += visibleCodes.length;
        return {
          ...subdomain,
          longTerms,
          visibleCodes,
          stats: { completed, total: visibleCodes.length }
        };
      });
      return {
        ...domain,
        subdomains,
        stats: { completed: domainCompleted, total: domainTotal }
      };
    });
  }, [domainTree, entriesMap, hasScore, longTermShortMap]);

  const eventsIndicator = getSaveIndicator(saveStatus);
  const saveDisabled = !assessment || assessment.status === "completed";
  const canSubmit = totalEntries > 0 && completedEntries === totalEntries && !saveDisabled;

  const toggleDomainCollapse = (code: string) => {
    setCollapsedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const toggleSubdomainCollapse = (key: string) => {
    setCollapsedSubdomains((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleScoreChange = (entry: ScaleEntry, score: 0 | 1 | 2) => {
    if (saveDisabled) return;
    const existing = entriesMap.get(entry.code);
    const wasUnscored = existing?.score === null || existing?.score === undefined;
    void saveEntry({ entryCode: entry.code, score })
      .then(() => {
        if (entry.level === "long_term") {
          setManualExpandedLongGoals((prev) => {
            const next = new Set(prev);
            if (score === 2) next.delete(entry.code);
            else next.add(entry.code);
            return next;
          });
        }
        if (!wasUnscored) return;

        if (entry.level === "long_term" && (score === 0 || score === 1)) {
          const group = domainTree
            .flatMap((domain) => domain.subdomains)
            .flatMap((subdomain) => subdomain.longTerms)
            .find((candidate) => candidate.entry.code === entry.code);
          setPendingScrollCode(group?.shortEntries[0]?.code);
          return;
        }

        const projectedEntries = projectEntryScore(entries, assessment?.id ?? "", entry.code, score);
        setPendingScrollCode(
          nextUnscoredEntryCode(
            requiredEntryCodes(scaleLibrary, projectedEntries),
            projectedEntries,
            entry.code,
          ),
        );
      })
      .catch(() => undefined);
  };

  const handleCommentChange = (entryCode: string, comment: string) => {
    if (saveDisabled) return;
    saveEntry({ entryCode, comment });
  };

  const handleSubmit = async () => {
    if (saveDisabled || !assessment) return;
    await markStatus("completed");
  };

  const forceExpandShortTerms = (code: string) => {
    setManualExpandedLongGoals((prev) => {
      if (prev.has(code)) return prev;
      const next = new Set(prev);
      next.add(code);
      return next;
    });
  };

  const sessionReady = assessmentBelongsToStudent(assessment, studentId, "scale");

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white/95 p-6 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div>
          <AssessmentBreadcrumbs
            currentPage="完整量表评估"
            studentName={studentQuery.data?.profile.name}
            studentId={studentId}
          />
          <h1 className="text-2xl font-semibold text-slate-800">完整量表评估</h1>
          <p className="mt-1 text-sm text-slate-500">按领域和条目逐项评分，修改自动保存。</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {assessment && sessionReady ? (
            <AssessmentEntryModeSwitch
              assessment={assessment}
              studentId={studentId}
              currentMode="scale"
              beforeSwitch={flushEntries}
            />
          ) : null}
          {eventsIndicator.label ? (
            <span className={`text-xs ${eventsIndicator.className}`}>{eventsIndicator.label}</span>
          ) : null}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {assessment?.status === "completed" ? "评估已提交" : marking ? "提交中…" : "提交评估"}
          </button>
        </div>
      </header>

      {!requestedAssessmentId ? (
        <AssessmentStartPanel studentId={studentId} />
      ) : assessmentLoading ? (
        <section className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          正在读取评估数据…
        </section>
      ) : assessmentError ? (
        <AssessmentStartPanel
          studentId={studentId}
          title="无法读取这份评估"
          description="评估记录不存在、无权访问或暂时无法加载。系统没有创建替代记录，请返回后重新选择。"
        />
      ) : !sessionReady ? (
        <AssessmentStartPanel
          studentId={studentId}
          title="儿童与评估记录不匹配"
          description="这份评估不属于当前儿童。为保护数据，页面不会显示或修改该记录，请返回当前儿童的评估列表重新选择。"
        />
      ) : (
        <section className="flex flex-col gap-6 xl:flex-row">
          <aside
            aria-label="量表评估目录"
            className="xl:sticky xl:top-4 xl:z-10 xl:flex xl:max-h-[calc(100dvh-7rem)] xl:w-80 xl:shrink-0 xl:self-start"
          >
            <div className="space-y-4 xl:flex xl:min-h-0 xl:w-full xl:flex-1 xl:flex-col xl:gap-4 xl:space-y-0">
              <div className="shrink-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                  <span>总体进度</span>
                  <span>
                    {completedEntries}/{totalEntries}
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-brand transition-all"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  长期目标得 0 或 1 分后，继续评估短期目标。
                </p>
              </div>

              <nav className="rounded-xl border border-slate-200 bg-white shadow-sm xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-contain">
                {navTree.map((domain) => {
                  const domainCollapsed = collapsedDomains.has(domain.domainCode);
                  return (
                    <div key={domain.domainCode} className="border-b border-slate-100 last:border-none">
                      <button
                        type="button"
                        onClick={() => toggleDomainCollapse(domain.domainCode)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{domain.domainName}</p>
                          <p className="text-xs text-slate-500">
                            已评 {domain.stats.completed}/{domain.stats.total}
                          </p>
                        </div>
                        <ChevronIcon expanded={!domainCollapsed} />
                      </button>
                      {!domainCollapsed ? (
                        <div className="border-t border-slate-100 px-3 py-2 text-sm">
                          {domain.subdomains.map((subdomain) => {
                            const subKey = `${domain.domainCode}:${subdomain.subdomainCode}`;
                            const subCollapsed = collapsedSubdomains.has(subKey);
                            return (
                              <div key={subKey} className="rounded-lg px-2 py-1">
                                <button
                                  type="button"
                                  onClick={() => toggleSubdomainCollapse(subKey)}
                                  className="flex w-full items-center justify-between text-left text-slate-700"
                                >
                                  <div>
                                    <p className="text-sm font-semibold">{subdomain.subdomainLabel}</p>
                                    <p className="text-xs text-slate-500">
                                      {subdomain.stats.completed}/{subdomain.stats.total}
                                    </p>
                                  </div>
                                  <ChevronIcon expanded={!subCollapsed} />
                                </button>
                                {!subCollapsed ? (
                                  <ul className="mt-2 space-y-1 border-l border-dashed border-slate-200 pl-3 text-xs">
                                    {subdomain.longTerms.map((group) => {
                                      const longRecord = entriesMap.get(group.entry.code);
                                      const longActive = activeEntryCode === group.entry.code;
                                      return (
                                        <li key={group.entry.code}>
                                          <button
                                            type="button"
                                            onClick={() => scrollToEntry(group.entry.code)}
                                            className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left ${
                                              longActive ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-50"
                                            }`}
                                          >
                                            <span className="line-clamp-2">
                                              {group.entry.code} · {getEntryTitle(group.entry)}
                                            </span>
                                            <StatusBadge record={longRecord} />
                                          </button>
                                          {group.showShorts ? (
                                            <ul className="mt-1 space-y-1 border-l border-slate-100 pl-3">
                                              {group.shortEntries.map((short) => {
                                                const shortRecord = entriesMap.get(short.code);
                                                const shortActive = activeEntryCode === short.code;
                                                return (
                                                  <li key={short.code}>
                                                    <button
                                                      type="button"
                                                      onClick={() => scrollToEntry(short.code)}
                                                      className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-left ${
                                                        shortActive
                                                          ? "bg-brand/10 text-brand"
                                                          : "text-slate-500 hover:bg-slate-50"
                                                      }`}
                                                    >
                                                      <span className="line-clamp-2">{short.shortTermGoal?.title}</span>
                                                      <StatusBadge record={shortRecord} />
                                                    </button>
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          ) : null}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </nav>
            </div>
          </aside>

          <div className="flex-1 space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            {entriesLoading ? (
              <p className="text-sm text-slate-500">正在加载评估条目…</p>
            ) : (
              domainTree.map((domain) => (
                <div key={domain.domainCode} className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {domain.domainName}
                    </p>
                  </div>
                  {domain.subdomains.map((subdomain) => (
                    <div key={subdomain.subdomainCode} className="space-y-4">
                      <h2 className="text-xl font-semibold text-slate-800">{subdomain.subdomainLabel}</h2>
                      <div className="space-y-6">
                        {subdomain.longTerms.map((group) => {
                          const longRecord = entriesMap.get(group.entry.code);
                          const showShorts = longTermShortMap.get(group.entry.code) ?? false;
                          const shouldShowManualHint = !showShorts && group.shortEntries.length > 0;
                          return (
                            <div key={group.entry.code} className="space-y-3">
                              <EntryEvaluationCard
                                entry={group.entry}
                                record={longRecord}
                                disabled={saveDisabled}
                                onScore={handleScoreChange}
                                onComment={handleCommentChange}
                                registerRef={registerEntryRef}
                                isActive={activeEntryCode === group.entry.code}
                                variant="long"
                              />
                              {shouldShowManualHint ? (
                                <button
                                  type="button"
                                  onClick={() => forceExpandShortTerms(group.entry.code)}
                                  className="text-sm text-brand underline-offset-4 hover:underline"
                                >
                                  展开短期目标
                                </button>
                              ) : null}
                              {showShorts ? (
                                <div className="space-y-3 border-l-2 border-dashed border-brand/30 pl-4">
                                  {group.shortEntries.map((short) => (
                                    <EntryEvaluationCard
                                      key={short.code}
                                      entry={short}
                                      record={entriesMap.get(short.code)}
                                      disabled={saveDisabled}
                                      onScore={handleScoreChange}
                                      onComment={handleCommentChange}
                                      registerRef={registerEntryRef}
                                      isActive={activeEntryCode === short.code}
                                      variant="short"
                                    />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function EntryEvaluationCard({
  entry,
  record,
  disabled,
  onScore,
  onComment,
  registerRef,
  isActive,
  variant
}: {
  entry: ScaleEntry;
  record?: AssessmentEntry;
  disabled: boolean;
  onScore: (entry: ScaleEntry, score: 0 | 1 | 2) => void;
  onComment: (entryCode: string, comment: string) => void;
  registerRef: (code: string, node: HTMLElement | null) => void;
  isActive: boolean;
  variant: "long" | "short";
}): JSX.Element {
  const [comment, setComment] = useState(record?.comment ?? "");

  useEffect(() => {
    setComment(record?.comment ?? "");
  }, [record?.comment, entry.code]);

  useEffect(() => {
    if (disabled) return;
    if (comment === (record?.comment ?? "")) {
      return;
    }
    const timer = setTimeout(() => {
      onComment(entry.code, comment);
    }, 600);
    return () => clearTimeout(timer);
  }, [comment, disabled, onComment, record?.comment, entry.code]);

  const goalTitle = getEntryTitle(entry);
  const status = record?.score;
  const containerClasses = [
    "rounded-2xl border-2 transition shadow-sm",
    variant === "long" ? "border-brand/30 bg-white p-6" : "border-slate-200 bg-slate-50 p-5",
    isActive ? "ring-2 ring-brand/40" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article
      id={`entry-${entry.code}`}
      ref={(node) => registerRef(entry.code, node)}
      className={containerClasses}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              {entry.domainName} · {entry.subdomainLabel} ·{" "}
              {entry.level === "long_term" ? "长期目标" : "短期目标"}
            </p>
            <div className="mt-2 flex flex-col gap-2 xl:flex-row xl:items-baseline xl:gap-4">
              <h3 className="text-xl font-semibold text-slate-900">{entry.code} · {goalTitle}</h3>
            </div>
          </div>
          <ScoreSelector
            score={status}
            disabled={disabled}
            onSelect={(value) => onScore(entry, value)}
          />
        </div>

        <div className="text-sm leading-7 text-slate-700">
          <p>{entry.standard}</p>
          {entry.examples ? (
            <p className="mt-2 text-xs text-slate-500">示例：{entry.examples}</p>
          ) : null}
          {entry.activities.length > 0 ? (
            <p className="mt-2 text-xs text-slate-500">
              推荐活动：<span className="text-slate-600">{entry.activities.join("、")}</span>
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">备注</div>
        <div className="relative mt-1">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onBlur={() => {
              if (comment !== (record?.comment ?? "")) onComment(entry.code, comment);
            }}
            rows={variant === "long" ? 4 : 3}
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-12 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:cursor-not-allowed disabled:bg-slate-100"
            placeholder="补充观察记录（自动保存）"
          />
          {!disabled ? (
            <VoiceInputButton
              size="sm"
              className="absolute bottom-2 right-2"
              initialValue={comment}
              onSave={setComment}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ScoreSelector({
  score,
  disabled,
  onSelect
}: {
  score?: number | null;
  disabled: boolean;
  onSelect: (value: 0 | 1 | 2) => void;
}): JSX.Element {
  return (
    <div className="w-full rounded-xl border border-brand/30 bg-white px-3 py-1.5 shadow-sm xl:w-[240px]">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-brand whitespace-nowrap">条目得分</span>
        <div className="flex items-center gap-2">
          {[0, 1, 2].map((value) => {
            const option = value as 0 | 1 | 2;
            const isSelected = score === option;
            return (
              <button
                key={option}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(option)}
                className={`h-9 w-9 rounded-full border text-sm font-semibold transition ${
                  isSelected
                    ? "border-brand bg-brand text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-brand hover:text-brand"
                } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ record }: { record?: AssessmentEntry }): JSX.Element {
  const score = record?.score;
  let label = "未评分";
  let className = "bg-slate-100 text-slate-500";
  if (score === 2) {
    label = "已掌握";
    className = "bg-brand/10 text-brand-dark";
  } else if (score === 1) {
    label = "发展中";
    className = "bg-sky-100 text-sky-800";
  } else if (score === 0) {
    label = "待提升";
    className = "bg-rose-100 text-rose-600";
  }
  return (
    <span
      className={`ml-2 flex-shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`h-4 w-4 text-slate-500 transition-transform ${expanded ? "rotate-90" : ""}`}
    >
      <path
        d="M7 5l6 5-6 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getEntryTitle(entry: ScaleEntry): string {
  return entry.shortTermGoal?.title ?? entry.longTermGoal.title;
}

function getSaveIndicator(status: ScaleAssessmentSaveStatus) {
  switch (status) {
    case "saving":
      return { label: "保存中…", className: "text-sky-700" };
    case "saved":
      return { label: "最新修改已保存", className: "text-brand-dark" };
    case "error":
      return { label: "保存失败，请重试", className: "text-red-600" };
    default:
      return { label: "", className: "text-slate-500" };
  }
}

function projectEntryScore(
  entries: readonly AssessmentEntry[],
  assessmentId: string,
  entryCode: string,
  score: 0 | 1 | 2,
): AssessmentEntry[] {
  const existing = entries.find((entry) => entry.entryCode === entryCode);
  if (existing) {
    return entries.map((entry) => (entry.entryCode === entryCode ? { ...entry, score } : entry));
  }
  return [
    ...entries,
    {
      assessmentId,
      entryCode,
      score,
      version: 0,
      updatedAt: "",
    },
  ];
}

function scrollIntoMainContent(target: HTMLElement): void {
  const container = document.getElementById("main-content");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!container) {
    target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTo({
    top: container.scrollTop + targetRect.top - containerRect.top - 16,
    behavior: reducedMotion ? "auto" : "smooth",
  });
}
